import {
  type CreateKbSourceRequest,
  KB_LIST_DEFAULT_LIMIT,
  kbIsSettling,
  KbSource,
  type KbTestRequest,
  KbTestResult,
  type PageMeta,
} from '@rp/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import type { ApiClient } from '../../api/client'
import { saveFile } from '../../lib/save-file'
import { useReadySession } from '../../session/useSession'

export const KB_PAGE_SIZE = KB_LIST_DEFAULT_LIMIT
export const KB_INGEST_POLL_MS = 2_000

export const kbKeys = {
  all: ['kb'] as const,
  sources: (tenantId: string, page: number) => ['kb', 'sources', tenantId, page] as const,
}

export type KbSourcePage = { sources: readonly KbSource[]; meta: PageMeta }

export const kbRefetchInterval = (data: KbSourcePage | undefined): number | false =>
  data?.sources.some((source) => kbIsSettling(source.status)) === true ? KB_INGEST_POLL_MS : false

export const useKbSources = (page: number) => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: kbKeys.sources(membership.tenantId, page),
    queryFn: async (): Promise<KbSourcePage> => {
      const result = await api.request(z.array(KbSource), {
        method: 'GET',
        path: '/v1/kb/sources',
        query: { page, limit: KB_PAGE_SIZE },
      })
      return { sources: result.data, meta: result.meta ?? { total: result.data.length, page, limit: KB_PAGE_SIZE } }
    },
    refetchInterval: (query) => kbRefetchInterval(query.state.data),
  })
}

export type UploadKbSourceInput = { kind: 'upload'; title: string; file: File }
export type AddKbSourceInput = CreateKbSourceRequest | UploadKbSourceInput

export const requestBodyOf = (input: AddKbSourceInput): CreateKbSourceRequest | FormData => {
  if (input.kind === 'url') return input
  const form = new FormData()
  form.set('title', input.title)
  form.set('file', input.file)
  return form
}

export const useAddKbSource = () => {
  const { api } = useReadySession()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: AddKbSourceInput) =>
      (await api.request(KbSource, { method: 'POST', path: '/v1/kb/sources', body: requestBodyOf(input) })).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export const useDeleteKbSource = () => {
  const { api } = useReadySession()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.request(z.null(), { method: 'DELETE', path: `/v1/kb/sources/${id}` })
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export const useRetryKbSource = () => {
  const { api } = useReadySession()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.request(KbSource, { method: 'POST', path: `/v1/kb/sources/${id}/retry` })).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export const downloadKbSource = async (api: ApiClient, source: Pick<KbSource, 'id' | 'title'>): Promise<void> => {
  const file = await api.download({ path: `/v1/kb/sources/${source.id}/file` })
  saveFile(file.blob, file.filename ?? source.title)
}

export const useKbTest = () => {
  const { api } = useReadySession()
  return useMutation({
    mutationFn: async (question: string) =>
      (await api.request(KbTestResult, { method: 'POST', path: '/v1/kb/test', body: { question } satisfies KbTestRequest })).data,
  })
}
